import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPut, apiPost, apiDel, api } from '../lib/api';
import { inrCompact, inr, formatDate, statusColor } from '../lib/format';
import { Plus, Upload, Link2, Save, FileSpreadsheet, RefreshCw, ChevronDown, ChevronRight, Edit2, X, Trash2, Download, CalendarRange, FilePlus2, AlertTriangle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import BOQUploadWizard from '../components/BOQUploadWizard';
import { useUrlTab } from '../lib/hooks/useUrlState';

export default function BOQSchedule({ project }) {
  const [tab, setTab] = useUrlTab('boqtab', 'BOQ');
  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex gap-2 text-xs font-display font-semibold uppercase tracking-widest">
          {['BOQ', 'Schedule View', 'Micro Schedule', 'MSP Tasks'].map(t => (
            <button key={t} data-testid={`boq-sub-${t.toLowerCase().replace(/\s+/g,'-')}`} onClick={() => setTab(t)}
              className={`px-3 py-1.5 rounded-sm border ${tab === t ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'border-slate-300 text-slate-600 hover:border-slate-400'}`}>
              {t}
            </button>
          ))}
        </div>
      </div>
      {tab === 'BOQ' && <BOQGrid project={project} />}
      {tab === 'Schedule View' && <BOQScheduleView project={project} />}
      {tab === 'Micro Schedule' && <MicroSchedule project={project} />}
      {tab === 'MSP Tasks' && <MSPPanel project={project} />}
    </div>
  );
}

function BOQGrid({ project }) {
  const { user } = useAuth();
  const isMD = user?.role === 'MD' || user?.role === 'super_admin';
  const [data, setData] = useState(null);
  const [editingExec, setEditingExec] = useState(null);
  const [exec, setExec] = useState('');
  const [importing, setImporting] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [collapsedNodes, setCollapsedNodes] = useState({}); // collapsed parent nodes
  const [expandedRow, setExpandedRow] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [monthlyView, setMonthlyView] = useState(false);
  const [extraModal, setExtraModal] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  const load = async () => {
    try { setData(await apiGet(`/boq/by-project/${project.id}`)); } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id]);

  const onUploadExcel = async (e, replace) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    const fd = new FormData();
    fd.append('file', f);
    try {
      const r = await api.post(`/boq/import-excel?project_id=${project.id}&replace=${replace ? 'true' : 'false'}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(r.data);
      toast.success(`Imported ${r.data.imported} BOQ items across ${r.data.sections.length} sections`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    }
    setImporting(false);
    e.target.value = '';
  };

  const toggleSection = (sec) => setCollapsedSections(p => ({ ...p, [sec]: !p[sec] }));
  const toggleNode = (id) => setCollapsedNodes(p => ({ ...p, [id]: !p[id] }));

  const onDeleteItem = async (id) => {
    if (!window.confirm('Delete this BOQ item?\n\nThis action cannot be undone.')) return;
    try {
      const r = await apiDel(`/boq/${id}`);
      toast.success('Deleted');
      const recomp = r?._project_recomputation;
      if (recomp?.applied_to_contract) {
        toast.message(`📊 Contract value updated to ${inrCompact(recomp.new)}`, { description: `${recomp.delta_pct.toFixed(1)}% from delete` });
      }
      load();
    }
    catch (e) { toast.error('Failed'); }
  };

  const onRecompute = async () => {
    if (!window.confirm('Recompute contract value from BOQ totals and apply to project?')) return;
    try {
      const r = await apiPost(`/projects/${project.id}/recompute-contract-value`, {});
      toast.success(`Contract value updated to ${inrCompact(r.recomputation.new)}`);
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get('/boq-sample-template', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Ellora_BOQ_Sample_Template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Sample BOQ template downloaded');
    } catch { toast.error('Download failed'); }
  };

  if (!data) return <div className="text-slate-400 py-8 text-center text-sm">Loading…</div>;

  // Group items by section, then build tree by parent_code
  const grouped = {};
  data.items.forEach(b => {
    const sec = b.section || 'Ungrouped';
    if (!grouped[sec]) grouped[sec] = { items: [], total: 0, earned: 0, codeIndex: {} };
    grouped[sec].items.push(b);
    grouped[sec].total += b.amount || 0;
    grouped[sec].earned += b.earned || 0;
    grouped[sec].codeIndex[b.item_code] = b;
  });
  const sections = Object.entries(grouped);

  // Build tree per section: flatten visible items into a list (cycle-safe via visited set)
  const flattenTree = (items, collapsedNodes) => {
    const childMap = {};
    items.forEach(it => {
      const p = it.parent_code || null;
      // Only treat as child if parent_code != own item_code and parent exists in section
      if (p && p !== it.item_code) {
        childMap[p] = childMap[p] || [];
        childMap[p].push(it);
      }
    });
    const itemCodes = new Set(items.map(i => i.item_code));
    const roots = items.filter(it => !it.parent_code || it.parent_code === it.item_code || !itemCodes.has(it.parent_code));

    const visible = [];
    const visited = new Set();
    const walk = (node, depth) => {
      if (visited.has(node.id)) return;
      visited.add(node.id);
      visible.push({ ...node, _depth: depth });
      const kids = childMap[node.item_code] || [];
      if (kids.length && !collapsedNodes[node.id] && depth < 8) {
        kids.forEach(k => walk(k, depth + 1));
      }
    };
    roots.forEach(r => walk(r, 0));
    // Append any orphans we missed (cycles)
    items.forEach(it => { if (!visited.has(it.id)) visible.push({ ...it, _depth: 0 }); });
    return { visible, hasChildrenSet: new Set(Object.keys(childMap).filter(k => k && childMap[k].length > 0)) };
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="kpi-overline">Bill of Quantities</div>
          <h3 className="font-display font-bold text-base mt-1">{data.items.length} items · {sections.length} sections {isMD && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#3b4872] bg-indigo-50 px-1.5 py-0.5 rounded-sm">MD · Edit Mode</span>}</h3>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setMonthlyView(v => !v)} data-testid="boq-monthly-toggle"
            className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-sm font-display font-semibold border ${monthlyView ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white border-slate-300 hover:border-[#3b4872] hover:text-[#3b4872]'}`}
            title="Monthly executed + billing breakdown from DPR & RA bills">
            <CalendarRange size={12} /> {monthlyView ? 'Standard View' : 'Monthly View'}
          </button>
          {isMD && (
            <button onClick={() => setEditModal({ project_id: project.id, isNew: true })} data-testid="boq-add-item-btn"
              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white border border-slate-300 hover:border-[#3b4872] hover:text-[#3b4872] rounded-sm font-display font-semibold">
              <Plus size={12} /> Add Item
            </button>
          )}
          {isMD && data?.items?.length > 0 && (
            <button onClick={onRecompute} data-testid="boq-recompute-btn"
              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white border border-slate-300 hover:border-[#3b4872] hover:text-[#3b4872] rounded-sm font-display font-semibold" title="Recompute project contract value from BOQ totals">
              <RefreshCw size={12} /> Recompute Contract
            </button>
          )}
          <button onClick={downloadTemplate} data-testid="boq-download-template"
            className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white border border-slate-300 hover:border-[#3b4872] hover:text-[#3b4872] rounded-sm font-display font-semibold" title="Download a sample BOQ Excel template with the correct format">
            <Download size={12} /> Sample Template
          </button>
          {isMD && (
            <button onClick={() => setWizardOpen(true)} data-testid="boq-smart-upload-btn"
              className="flex items-center gap-2 px-3 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm hover:bg-[#2c3658] font-display font-semibold"
              title="Universal BOQ upload — works with any Excel layout">
              <Sparkles size={13} /> {data?.items?.length > 0 ? 'Smart Upload (Replace/Append)' : 'Smart Upload BOQ'}
            </button>
          )}
        </div>
      </div>

      {importResult && (
        <div className="bg-emerald-50 border-l-4 border-l-emerald-500 px-4 py-3 mb-4 text-xs text-emerald-900">
          <div className="font-display font-bold uppercase tracking-widest text-[10px] mb-1">Last import · {importResult.filename}</div>
          {importResult.imported} items imported · ₹{Math.round(importResult.total_value/10000000).toLocaleString('en-IN')} Cr total · {importResult.sections.length} sections · {importResult.skipped_rows || 0} note rows skipped
        </div>
      )}

      {project.boq_carried_over && (
        <div className="bg-blue-50 border-l-4 border-l-blue-500 px-4 py-3 mb-4 text-xs text-blue-900" data-testid="boq-carried-over-banner">
          This BOQ was carried forward automatically from the awarded
          {project.source_tender_id ? ' tender' : ' enquiry'} ({project.boq_item_count} items).
          {isMD && (
            <button
              onClick={() => setWizardOpen(true)}
              className="ml-2 underline font-semibold hover:text-blue-700"
              data-testid="boq-replace-btn"
            >
              Re-map or replace with a different file
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Mini label="BOQ Items" value={data.items.length} />
        <Mini label="Total BOQ Value" value={inrCompact(data.total_value)} />
        <Mini label="Earned Value" value={inrCompact(data.earned_value)} sub={`${data.physical_progress}% physical progress`} />
        <Mini label="Balance to Execute" value={inrCompact(data.total_value - data.earned_value)} />
      </div>

      {data.items.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-sm py-16 text-center">
          <FileSpreadsheet size={32} className="mx-auto text-slate-300 mb-3" />
          <div className="kpi-overline mb-2">No BOQ Yet</div>
          <div className="text-sm text-slate-500 mb-4 max-w-md mx-auto">
            {isMD ? "Upload your client BOQ Excel — we'll parse all sections and items automatically. Need a starting point? Download the sample template below."
                  : "No BOQ has been uploaded yet. Only the MD can upload or replace the master BOQ."}
          </div>
          <div className="flex items-center justify-center gap-2">
            <button onClick={downloadTemplate} className="inline-flex items-center gap-2 px-4 py-2 text-xs bg-white border border-slate-300 hover:border-[#3b4872] hover:text-[#3b4872] text-slate-700 rounded-sm font-display font-semibold">
              <Download size={13} /> Download Sample Template
            </button>
            {isMD && (
              <button onClick={() => setWizardOpen(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 text-xs bg-[#3b4872] text-white rounded-sm hover:bg-[#2c3658] font-display font-semibold"
                      data-testid="boq-empty-smart-upload-btn">
                <Sparkles size={13} /> Smart Upload BOQ
              </button>
            )}
          </div>
        </div>
      ) : monthlyView ? (
        <BOQMonthlyView project={project} onExited={() => setMonthlyView(false)} />
      ) : (
        <div className="space-y-3">
          {sections.map(([sec, g]) => {
            const isCollapsed = collapsedSections[sec];
            const sectionProgress = g.total > 0 ? Math.round((g.earned / g.total) * 100) : 0;
            const { visible: visibleRows, hasChildrenSet } = flattenTree(g.items, collapsedNodes);
            return (
              <div key={sec} className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid={`boq-section-${sec.slice(0,20).replace(/[^a-z0-9]/gi,'-')}`}>
                <button onClick={() => toggleSection(sec)} className="w-full px-4 py-2.5 bg-gradient-to-r from-indigo-50/40 to-transparent hover:bg-slate-100 border-b border-slate-200 flex items-center justify-between gap-3 text-left">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {isCollapsed ? <ChevronRight size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
                    <span className="font-display font-extrabold text-sm truncate text-[#0F172A]">{sec}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs shrink-0">
                    <span className="text-slate-500">{g.items.length} items</span>
                    <span className="font-display font-extrabold tabular-nums">{inrCompact(g.total)}</span>
                    <div className="flex items-center gap-1.5 w-32">
                      <div className="flex-1 h-1 bg-slate-200 rounded-sm overflow-hidden">
                        <div className="h-full bg-[#3b4872]" style={{ width: `${sectionProgress}%` }} />
                      </div>
                      <span className="font-display font-bold tabular-nums w-8">{sectionProgress}%</span>
                    </div>
                  </div>
                </button>
                {!isCollapsed && (
                  <div className="overflow-x-auto">
                    <table className="w-full dense-table">
                      <thead><tr>
                        <th style={{ width: 200 }}>Code</th>
                        <th>Description</th>
                        <th style={{ width: 60 }}>Unit</th>
                        <th className="text-right" style={{ width: 80 }}>Qty</th>
                        <th className="text-right" style={{ width: 90 }}>Rate</th>
                        <th className="text-right" style={{ width: 100 }}>Amount</th>
                        <th className="text-right" style={{ width: 90 }}>Executed</th>
                        <th style={{ width: 100 }}>Progress</th>
                        <th className="text-right" style={{ width: 90 }}>Earned</th>
                        <th style={{ width: 80 }}></th>
                      </tr></thead>
                      <tbody>
                        {visibleRows.map(item => {
                          const hasChildren = hasChildrenSet.has(item.item_code);
                          const indent = (item._depth || 0) * 18;
                          const isCollapsedNode = !!collapsedNodes[item.id];
                          const isExpanded = expandedRow === item.id;
                          const isGroup = item.is_group;
                          const rowBg = isGroup ? 'bg-slate-50/70 hover:bg-slate-100' : 'hover:bg-indigo-50/30';
                          return (
                            <React.Fragment key={item.id}>
                              <tr data-testid={`boq-row-${item.id}`} className={`${rowBg} border-b border-slate-100`}>
                                <td className="font-mono text-[10px] align-top" style={{ paddingLeft: 12 + indent }}>
                                  <div className="flex items-center gap-1">
                                    {hasChildren ? (
                                      <button onClick={() => toggleNode(item.id)} className="text-slate-400 hover:text-[#3b4872]">
                                        {isCollapsedNode ? <ChevronRight size={11} /> : <ChevronDown size={11} />}
                                      </button>
                                    ) : <span className="w-3 inline-block" />}
                                    <span className={isGroup ? 'font-bold text-[#3b4872]' : ''}>{item.item_code}</span>
                                  </div>
                                </td>
                                <td className="text-xs align-top">
                                  <button onClick={() => setExpandedRow(isExpanded ? null : item.id)} className="text-left w-full hover:text-[#3b4872] transition-colors" data-testid={`boq-expand-${item.id}`}>
                                    <div className={isExpanded ? '' : 'line-clamp-2'} title={item.description}>
                                      {isGroup && <span className="font-display font-bold uppercase tracking-wider text-[10px] text-[#3b4872] mr-1.5">GROUP</span>}
                                      {item.description}
                                    </div>
                                  </button>
                                </td>
                                <td className="text-xs align-top">{item.unit}</td>
                                <td className="text-right tabular-nums align-top">{(item.quantity || 0).toLocaleString('en-IN')}</td>
                                <td className="text-right tabular-nums font-mono text-xs align-top">{item.rate ? inr(item.rate) : '—'}</td>
                                <td className="text-right tabular-nums font-display font-bold align-top">{item.amount ? inrCompact(item.amount) : '—'}</td>
                                <td className="text-right align-top">
                                  {!isGroup && (
                                    <span
                                      className="font-display font-semibold tabular-nums text-xs text-slate-700"
                                      title="Read-only — rolled up from WBS (v44)"
                                      data-testid={`boq-exec-qty-${item.id}`}
                                    >
                                      {(item.executed_qty || 0).toLocaleString('en-IN')}
                                    </span>
                                  )}
                                </td>
                                <td className="align-top">
                                  {!isGroup && (
                                    <div className="flex items-center gap-2">
                                      <div className="w-12 h-1 bg-slate-100 rounded-sm overflow-hidden">
                                        <div className="h-full" style={{ width: `${item.progress || 0}%`, background: (item.progress || 0) >= 80 ? '#10B981' : '#3b4872' }} />
                                      </div>
                                      <span className="text-xs font-display font-bold tabular-nums w-8">{item.progress || 0}%</span>
                                    </div>
                                  )}
                                </td>
                                <td className="text-right tabular-nums text-xs align-top">{!isGroup && item.earned ? inrCompact(item.earned) : ''}</td>
                                <td className="align-top">
                                  <div className="flex items-center gap-1 justify-end">
                                    {isMD && (
                                      <>
                                        <button onClick={() => setEditModal({ ...item, isNew: false })} data-testid={`boq-edit-${item.id}`} className="text-slate-400 hover:text-[#3b4872]" title="Edit (MD only)"><Edit2 size={11} /></button>
                                        <button onClick={() => onDeleteItem(item.id)} className="text-slate-400 hover:text-red-600" title="Delete (MD only)"><Trash2 size={11} /></button>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                              {isExpanded && (
                                <tr className="bg-slate-50/80">
                                  <td></td>
                                  <td colSpan="9" className="py-3 px-3 border-l-2 border-l-[#3b4872]">
                                    <div className="text-xs space-y-1.5 max-w-3xl">
                                      <div className="kpi-overline">Full Description</div>
                                      <div className="text-sm whitespace-pre-line">{item.description}</div>
                                      <div className="flex flex-wrap gap-3 text-[11px] text-slate-500 pt-2 border-t border-slate-200 mt-2">
                                        {item.sr_no && <span><b>Sr.No:</b> {item.sr_no}</span>}
                                        {item.parent_code && <span><b>Parent:</b> {item.parent_code}</span>}
                                        {item.level !== undefined && <span><b>Level:</b> {item.level}</span>}
                                        {item.imported_from && <span><b>Source:</b> {item.imported_from}</span>}
                                        {item.edited_by && <span className="text-[#3b4872]"><b>Last edited:</b> {item.edited_by}</span>}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
          <div className="bg-indigo-50/50 border border-indigo-200 rounded-sm px-4 py-3 flex items-center justify-between font-display font-bold">
            <span>GRAND TOTAL · {data.items.length} items</span>
            <div className="flex items-center gap-6">
              <span className="tabular-nums">{inrCompact(data.total_value)}</span>
              <span className="text-emerald-700">Earned {inrCompact(data.earned_value)} ({data.physical_progress}%)</span>
            </div>
          </div>
        </div>
      )}

      {/* Add Extra Item / Variation — billable scope not in the original BOQ */}
      {data.items.length > 0 && (
        <div className="mt-4 flex justify-end">
          <button onClick={() => setExtraModal(true)} data-testid="boq-add-extra-item-btn"
            className="flex items-center gap-1.5 px-4 py-2 text-xs bg-amber-50 border border-amber-300 text-amber-800 hover:bg-amber-100 rounded-sm font-display font-semibold">
            <FilePlus2 size={13} /> Add Extra Item / Variation
          </button>
        </div>
      )}

      {editModal && <BoqEditModal data={editModal} onClose={() => setEditModal(null)} onSaved={() => { setEditModal(null); load(); }} />}
      {extraModal && <ExtraItemModal projectId={project.id} onClose={() => setExtraModal(false)} onSaved={() => { setExtraModal(false); load(); }} />}
      {wizardOpen && (
        <BOQUploadWizard
          projectId={project.id}
          hasExistingBoq={(data?.items?.length || 0) > 0}
          onClose={() => setWizardOpen(false)}
          onImported={(r) => { setImportResult(r); load(); }}
        />
      )}
    </div>
  );
}


function BoqEditModal({ data, onClose, onSaved }) {
  const [form, setForm] = useState({
    section: data.section || '',
    item_code: data.item_code || '',
    description: data.description || '',
    unit: data.unit || '',
    quantity: data.quantity ?? '',
    rate: data.rate ?? '',
  });
  const [busy, setBusy] = useState(false);
  const isNew = data.isNew;
  const projectId = data.project_id;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let resp;
      if (isNew) {
        resp = await apiPost('/boq', {
          project_id: projectId,
          ...form,
          quantity: Number(form.quantity || 0),
          rate: Number(form.rate || 0),
        });
        toast.success('Item added');
      } else {
        resp = await apiPut(`/boq/${data.id}`, {
          ...form,
          quantity: Number(form.quantity || 0),
          rate: Number(form.rate || 0),
        });
        toast.success('Item updated');
      }
      // If contract_value was recomputed and applied, surface the change
      const recomp = resp?._project_recomputation;
      if (recomp?.applied_to_contract) {
        const pct = recomp.delta_pct.toFixed(1);
        toast.message(`📊 Contract value updated to ${inrCompact(recomp.new)}`, {
          description: `${recomp.delta >= 0 ? '+' : ''}${pct}% from BOQ recomputation`,
          duration: 6000,
        });
      } else if (recomp && Math.abs(recomp.delta_pct) >= 0.1) {
        toast.message(`📊 BOQ value now ${inrCompact(recomp.new)}`, {
          description: `Project contract value left unchanged (shift was only ${recomp.delta_pct.toFixed(1)}%)`,
        });
      }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };

  const amount = (Number(form.quantity || 0) * Number(form.rate || 0));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-2xl">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">MD · BOQ {isNew ? 'Add' : 'Edit'}</div>
            <h3 className="font-display font-bold text-xl mt-1">{isNew ? 'Add New Line Item' : data.item_code}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="kpi-overline mb-1 block">Section</label>
            <input value={form.section} onChange={e => setForm({...form, section: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </div>
          <div>
            <label className="kpi-overline mb-1 block">Item Code</label>
            <input value={form.item_code} onChange={e => setForm({...form, item_code: e.target.value})} disabled={!isNew} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono disabled:bg-slate-50" />
          </div>
          <div>
            <label className="kpi-overline mb-1 block">Unit</label>
            <input value={form.unit} onChange={e => setForm({...form, unit: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </div>
          <div className="col-span-2">
            <label className="kpi-overline mb-1 block">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={4} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm resize-none" />
          </div>
          <div>
            <label className="kpi-overline mb-1 block">Quantity</label>
            <input type="number" step="any" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} data-testid="boq-edit-qty"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm tabular-nums" />
          </div>
          <div>
            <label className="kpi-overline mb-1 block">Rate (₹)</label>
            <input type="number" step="any" value={form.rate} onChange={e => setForm({...form, rate: e.target.value})} data-testid="boq-edit-rate"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm tabular-nums" />
          </div>
          <div className="col-span-2 bg-indigo-50/40 border border-indigo-200 rounded-sm px-3 py-2 flex items-center justify-between">
            <span className="kpi-overline">Computed Amount</span>
            <span className="font-display font-extrabold tabular-nums">{inrCompact(amount)}</span>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-100 font-display">Cancel</button>
          <button type="submit" disabled={busy} data-testid="boq-edit-save" className="px-4 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold disabled:opacity-50">
            {busy ? 'Saving…' : (isNew ? 'Add Item' : 'Save Changes')}
          </button>
        </div>
      </form>
    </div>
  );
}


const MONTH_LABEL = (m) => {
  const [y, mo] = m.split('-');
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleString('en-IN', { month: 'short', year: '2-digit' });
};

function ItemTypeBadge({ type }) {
  if (type === 'variation') return <span className="ml-1.5 text-[9px] uppercase tracking-wider font-bold text-sky-700 bg-sky-50 border border-sky-200 px-1 py-0.5 rounded-sm" data-testid="boq-badge-vo">VO</span>;
  if (type === 'extra') return <span className="ml-1.5 text-[9px] uppercase tracking-wider font-bold text-amber-800 bg-amber-100 border border-amber-300 px-1 py-0.5 rounded-sm" data-testid="boq-badge-extra">Extra</span>;
  return null;
}

function BOQMonthlyView({ project }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    setData(null); setErr(false);
    apiGet(`/boq/by-project/${project.id}/monthly-summary`).then(setData).catch(() => setErr(true));
  }, [project.id]);

  if (err) return <div className="text-rose-600 py-8 text-center text-sm">Could not load the monthly summary.</div>;
  if (!data) return <div className="text-slate-400 py-8 text-center text-sm">Loading monthly billing view…</div>;

  const { months, items, totals } = data;
  const leaves = items.filter(i => !i.is_group);

  return (
    <div className="space-y-3" data-testid="boq-monthly-view">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Mini label="Total Executed Value" value={inrCompact(totals.total_billed_amount + totals.total_unbilled_amount)} />
        <Mini label="Billed" value={inrCompact(totals.total_billed_amount)} sub={`${totals.total_billed.toLocaleString('en-IN')} qty in approved RA bills`} />
        <Mini label="Unbilled (opportunity)" value={inrCompact(totals.total_unbilled_amount)} sub={`${totals.total_unbilled.toLocaleString('en-IN')} qty executed, not yet billed`} />
        <Mini label="Months tracked" value={months.length || '—'} />
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full dense-table" data-testid="boq-monthly-table">
          <thead><tr>
            <th style={{ minWidth: 220 }}>Item</th>
            <th style={{ width: 50 }}>Unit</th>
            <th className="text-right" style={{ width: 80 }}>Total Qty</th>
            <th className="text-right" style={{ width: 80 }}>Rate</th>
            {months.map(m => <th key={m} className="text-right" style={{ width: 70 }}>{MONTH_LABEL(m)}</th>)}
            <th className="text-right" style={{ width: 80 }}>Executed</th>
            <th className="text-right" style={{ width: 80 }}>Billed</th>
            <th className="text-right" style={{ width: 80 }}>Unbilled</th>
            <th className="text-right" style={{ width: 90 }}>Billed Amt</th>
            <th style={{ width: 90 }}>% Billed</th>
          </tr></thead>
          <tbody>
            {leaves.map(it => {
              const opportunity = it.unbilled_qty > 0;
              return (
                <tr key={it.id} data-testid={`boq-monthly-row-${it.id}`}
                  className={`border-b border-slate-100 ${opportunity ? 'bg-amber-50/50' : 'hover:bg-slate-50'}`}>
                  <td className="text-xs align-top">
                    <span className="font-mono text-[10px] text-slate-400 mr-1">{it.item_code}</span>
                    <span className="line-clamp-2" title={it.description}>{it.description}</span>
                    <ItemTypeBadge type={it.item_type} />
                    {it.billing_status === 'pending_client_approval' && (
                      <span className="ml-1.5 text-[9px] uppercase tracking-wider font-bold text-rose-700 bg-rose-50 border border-rose-200 px-1 py-0.5 rounded-sm">At risk</span>
                    )}
                  </td>
                  <td className="text-xs align-top">{it.unit}</td>
                  <td className="text-right tabular-nums align-top">{(it.quantity || 0).toLocaleString('en-IN')}</td>
                  <td className="text-right tabular-nums font-mono text-xs align-top">{it.rate ? inr(it.rate) : '—'}</td>
                  {months.map(m => (
                    <td key={m} className="text-right tabular-nums text-xs align-top text-slate-600">
                      {it.monthly_executed?.[m] ? it.monthly_executed[m].toLocaleString('en-IN') : '·'}
                    </td>
                  ))}
                  <td className="text-right tabular-nums font-display font-bold align-top">{(it.executed_qty || 0).toLocaleString('en-IN')}</td>
                  <td className="text-right tabular-nums font-semibold text-emerald-700 align-top">{it.billed_qty ? it.billed_qty.toLocaleString('en-IN') : '0'}</td>
                  <td className="text-right tabular-nums font-semibold text-amber-700 align-top">
                    {it.billed_ahead ? <span title="Billed ahead of execution">0*</span> : (it.unbilled_qty ? it.unbilled_qty.toLocaleString('en-IN') : '0')}
                  </td>
                  <td className="text-right tabular-nums text-xs align-top">{it.billed_amount ? inrCompact(it.billed_amount) : '—'}</td>
                  <td className="align-top">
                    <div className="flex items-center gap-1.5">
                      <div className="w-12 h-1 bg-slate-100 rounded-sm overflow-hidden">
                        <div className="h-full bg-emerald-500" style={{ width: `${Math.min(it.pct_billed || 0, 100)}%` }} />
                      </div>
                      <span className="text-[10px] font-display font-bold tabular-nums w-8">{Math.round(it.pct_billed || 0)}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-indigo-50/60 font-display font-bold text-xs">
              <td colSpan={4} className="py-2">TOTALS</td>
              {months.map(m => (
                <td key={m} className="text-right tabular-nums py-2">{totals.by_month[m]?.executed ? totals.by_month[m].executed.toLocaleString('en-IN') : '·'}</td>
              ))}
              <td className="text-right tabular-nums py-2">{totals.total_executed.toLocaleString('en-IN')}</td>
              <td className="text-right tabular-nums py-2 text-emerald-700">{totals.total_billed.toLocaleString('en-IN')}</td>
              <td className="text-right tabular-nums py-2 text-amber-700">{totals.total_unbilled.toLocaleString('en-IN')}</td>
              <td className="text-right tabular-nums py-2">{inrCompact(totals.total_billed_amount)}</td>
              <td className="py-2"></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="text-[11px] text-slate-400 px-1">
        Month columns = qty executed that month (from DPR). <span className="text-emerald-700 font-semibold">Billed</span> = qty in approved/certified RA bills ·
        <span className="text-amber-700 font-semibold"> Unbilled</span> = executed − billed (amber rows are billing opportunities). <b>0*</b> = billed ahead of execution.
      </p>
    </div>
  );
}

function ExtraItemModal({ projectId, onClose, onSaved }) {
  const [form, setForm] = useState({
    description: '', unit: '', quantity: '', rate: '',
    item_type: 'variation', variation_order_id: '', justification: '',
  });
  const [vos, setVos] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    apiGet(`/variation-orders?project_id=${projectId}`).then(r => {
      const list = Array.isArray(r) ? r : (r.items || r.variation_orders || []);
      setVos(list.filter(v => (v.project_id || projectId) === projectId));
    }).catch(() => setVos([]));
  }, [projectId]);

  const amount = Number(form.quantity || 0) * Number(form.rate || 0);
  const submit = async (e) => {
    e.preventDefault();
    if (form.item_type === 'variation' && !form.variation_order_id) { toast.error('Select a Variation Order'); return; }
    if (form.item_type === 'extra' && !form.justification.trim()) { toast.error('Justification is required for an extra item'); return; }
    setBusy(true);
    try {
      const r = await apiPost(`/boq/by-project/${projectId}/add-extra-item`, {
        description: form.description, unit: form.unit,
        quantity: Number(form.quantity || 0), rate: Number(form.rate || 0),
        item_type: form.item_type,
        variation_order_id: form.item_type === 'variation' ? form.variation_order_id : null,
        justification: form.item_type === 'extra' ? form.justification : null,
      });
      toast.success(`${form.item_type === 'variation' ? 'Variation' : 'Extra'} item ${r.item_code} added`);
      const recomp = r?._project_recomputation;
      if (recomp?.applied_to_contract) toast.message(`📊 Contract value updated to ${inrCompact(recomp.new)}`);
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add item'); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-2xl" data-testid="boq-extra-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-amber-700">Extra Work · Variation</div>
            <h3 className="font-display font-bold text-xl mt-1">Add Extra Item</h3>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        <div className="px-5 pt-4">
          <div className="bg-amber-50 border-l-4 border-amber-500 px-3 py-2.5 rounded-sm text-xs text-amber-900 flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" />
            <div>Extra items require client approval for billing. Link to an approved <b>Variation Order</b> to strengthen your billing position — otherwise the item is flagged <b>billing at risk</b>.</div>
          </div>
        </div>

        <div className="p-5 grid grid-cols-2 gap-3">
          <div className="col-span-2 flex gap-2">
            {['variation', 'extra'].map(t => (
              <button key={t} type="button" onClick={() => set('item_type', t)} data-testid={`boq-extra-type-${t}`}
                className={`flex-1 px-3 py-2 text-xs font-display font-semibold rounded-sm border capitalize ${form.item_type === t ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white border-slate-300 text-slate-600'}`}>
                {t === 'variation' ? 'Variation (linked to VO)' : 'Extra (pending approval)'}
              </button>
            ))}
          </div>

          {form.item_type === 'variation' && (
            <div className="col-span-2">
              <label className="kpi-overline mb-1 block">Variation Order *</label>
              <select value={form.variation_order_id} onChange={e => set('variation_order_id', e.target.value)} data-testid="boq-extra-vo"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
                <option value="">— Select Variation Order —</option>
                {vos.map(v => <option key={v.id} value={v.id}>{v.vo_no || v.number || v.id.slice(0, 8)} · {v.title || v.description || ''} {v.status ? `(${v.status})` : ''}</option>)}
              </select>
              {vos.length === 0 && <p className="text-[11px] text-slate-400 mt-1">No variation orders found for this project. Create one first, or use the "Extra" type with a justification.</p>}
            </div>
          )}
          {form.item_type === 'extra' && (
            <div className="col-span-2">
              <label className="kpi-overline mb-1 block">Justification *</label>
              <textarea value={form.justification} onChange={e => set('justification', e.target.value)} rows={2} data-testid="boq-extra-justification"
                placeholder="Why is this extra work needed? (client instruction, site condition, design change…)"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm resize-none" />
            </div>
          )}

          <div className="col-span-2">
            <label className="kpi-overline mb-1 block">Description *</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2} required data-testid="boq-extra-description"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm resize-none" />
          </div>
          <div>
            <label className="kpi-overline mb-1 block">Unit *</label>
            <input value={form.unit} onChange={e => set('unit', e.target.value)} required data-testid="boq-extra-unit"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </div>
          <div>
            <label className="kpi-overline mb-1 block">Quantity *</label>
            <input type="number" step="any" value={form.quantity} onChange={e => set('quantity', e.target.value)} required data-testid="boq-extra-qty"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm tabular-nums" />
          </div>
          <div className="col-span-2 grid grid-cols-2 gap-3">
            <div>
              <label className="kpi-overline mb-1 block">Rate (₹) *</label>
              <input type="number" step="any" value={form.rate} onChange={e => set('rate', e.target.value)} required data-testid="boq-extra-rate"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm tabular-nums" />
            </div>
            <div className="bg-indigo-50/40 border border-indigo-200 rounded-sm px-3 py-2 flex flex-col justify-center">
              <span className="kpi-overline">Amount</span>
              <span className="font-display font-extrabold tabular-nums">{inrCompact(amount)}</span>
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-100 font-display">Cancel</button>
          <button type="submit" disabled={busy} data-testid="boq-extra-save" className="px-4 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold disabled:opacity-50">
            {busy ? 'Adding…' : 'Add Item'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Mini({ label, value, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="kpi-overline">{label}</div>
      <div className="font-display text-xl font-extrabold tracking-tight mt-1">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}


// ─── BOQ Schedule View (Layer 6) ─────────────────────────────────────────────

function BOQScheduleView({ project }) {
  const [period, setPeriod] = useState('monthly');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      const res = await apiGet(`/boq/project/${project.id}/schedule-view?${params.toString()}`);
      setData(res);
    } catch {
      toast.error('Failed to load schedule view');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id, period]);

  return (
    <div className="space-y-4" data-testid="boq-schedule-view">
      {/* Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1">
          {['weekly', 'monthly', 'quarterly'].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              data-testid={`schedule-period-${p}`}
              className={`px-2.5 py-1 text-xs font-display font-bold uppercase tracking-wider rounded-sm border ${period === p ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'border-slate-200 text-slate-600 hover:border-[#3b4872]'}`}
            >
              {p}
            </button>
          ))}
        </div>
        <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="text-xs border border-slate-200 rounded-sm px-2 py-1 outline-none focus:border-[#3b4872]" />
        <span className="text-xs text-slate-400">to</span>
        <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="text-xs border border-slate-200 rounded-sm px-2 py-1 outline-none focus:border-[#3b4872]" />
        <button onClick={load} className="flex items-center gap-1 px-3 py-1 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold" disabled={loading}>
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Note */}
      <div className="text-xs text-slate-500 bg-slate-50 px-3 py-2 rounded-lg border border-slate-100">
        <strong>v44 Schedule View</strong> — Actual quantities are derived from <strong>WBS.daily_actuals</strong> (updated by DPR sync-wbs).
        Planned quantities show the total WBS planned for each BOQ item.
      </div>

      {loading && <div className="text-center py-8 text-sm text-slate-400">Loading…</div>}

      {!loading && data && data.items.length === 0 && (
        <div className="text-center py-8 text-sm text-slate-400">
          No BOQ-linked WBS activities found. Link WBS tasks to BOQ items and push DPR actuals via sync-wbs.
        </div>
      )}

      {!loading && data && data.items.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-xs text-left font-display">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="sticky left-0 bg-slate-50 px-3 py-2 font-bold text-slate-600 min-w-[220px]">Item</th>
                <th className="px-2 py-2 font-bold text-slate-600 text-center">Unit</th>
                <th className="px-2 py-2 font-bold text-slate-600 text-right">Contract Qty</th>
                <th className="px-2 py-2 font-bold text-slate-600 text-right">Planned Total</th>
                <th className="px-2 py-2 font-bold text-slate-600 text-right">Actual Total</th>
                {(data.period_labels || []).map(lbl => (
                  <th key={lbl} className="px-2 py-2 font-bold text-center text-[#3b4872] min-w-[90px]">{lbl}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.items.map(row => {
                const pct = row.contract_qty > 0 ? Math.round(row.actual_qty_total / row.contract_qty * 100) : 0;
                return (
                  <tr key={row.boq_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="sticky left-0 bg-white px-3 py-2 font-medium text-slate-800 max-w-[220px] truncate" title={row.description}>
                      {row.description}
                    </td>
                    <td className="px-2 py-2 text-center text-slate-500">{row.unit}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{(row.contract_qty || 0).toLocaleString('en-IN')}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-slate-500">{row.planned_qty_total?.toLocaleString('en-IN') || 0}</td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      <div className="flex items-center justify-end gap-2">
                        <span className={pct >= 100 ? 'text-emerald-700 font-bold' : pct >= 80 ? 'text-amber-600' : 'text-slate-800'}>
                          {row.actual_qty_total?.toLocaleString('en-IN') || 0}
                        </span>
                        <span className={`text-[10px] px-1 rounded-sm ${pct >= 100 ? 'bg-emerald-100 text-emerald-700' : pct >= 80 ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-500'}`}>
                          {pct}%
                        </span>
                      </div>
                    </td>
                    {(data.period_labels || []).map(lbl => {
                      const p = row.periods?.find(x => x.label === lbl);
                      const actual = p?.actual || 0;
                      return (
                        <td key={lbl} className="px-2 py-2 text-right tabular-nums">
                          {actual > 0 ? (
                            <span className="text-emerald-700 font-semibold">{actual.toLocaleString('en-IN')}</span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function MicroSchedule({ project }) {
  const [data, setData] = useState(null);
  useEffect(() => { apiGet(`/boq/by-project/${project.id}`).then(setData); /* eslint-disable-next-line */ }, [project.id]);
  if (!data) return <div className="text-slate-400 py-8 text-center text-sm">Loading…</div>;

  // Generate 12 weeks ahead
  const weeks = Array.from({ length: 12 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i * 7);
    return d;
  });

  return (
    <div>
      <div className="kpi-overline mb-3">Micro Schedule · weekly planned vs actual quantity (synthetic distribution)</div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="min-w-[900px] w-full">
          <thead>
            <tr className="bg-slate-50">
              <th className="kpi-overline text-left p-3 sticky left-0 bg-slate-50 border-r border-slate-200 w-72">BOQ Item</th>
              <th className="kpi-overline p-3 border-r border-slate-200">Unit</th>
              {weeks.map((w, i) => (
                <th key={i} className="kpi-overline p-2 text-center border-r border-slate-100 last:border-r-0 min-w-[60px]">
                  W{i + 1}<br /><span className="text-[9px] font-mono text-slate-400">{w.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.items.slice(0, 8).map((b, idx) => {
              // Fake weekly distribution — total qty / 12 with some variation
              const weekQty = b.quantity / 12;
              return (
                <tr key={b.id} className="border-t border-slate-100">
                  <td className="text-sm p-3 sticky left-0 bg-white border-r border-slate-200">
                    <div className="font-mono text-[10px] text-slate-500">{b.item_code}</div>
                    <div className="text-xs font-medium truncate max-w-[260px]">{b.description}</div>
                  </td>
                  <td className="text-xs text-center p-2 border-r border-slate-200">{b.unit}</td>
                  {weeks.map((_, i) => {
                    const planned = Math.round(weekQty * (1 + (i % 3 - 1) * 0.15));
                    const actual = i < 3 ? Math.round(planned * (0.85 + (idx % 3) * 0.05)) : 0;
                    const ratio = planned ? Math.min(actual / planned, 1.2) : 0;
                    return (
                      <td key={i} className="p-1 text-center border-r border-slate-100 last:border-r-0">
                        <div className="text-[10px] font-display font-bold leading-tight">{planned.toLocaleString('en-IN')}</div>
                        {actual > 0 && (
                          <div className={`text-[9px] font-mono ${ratio >= 0.95 ? 'text-emerald-700' : ratio >= 0.8 ? 'text-amber-700' : 'text-red-600'}`}>
                            {actual.toLocaleString('en-IN')}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-3 text-xs text-slate-500 flex items-center gap-4">
        <span><span className="inline-block w-2 h-2 bg-slate-700 mr-1" /> Planned qty</span>
        <span><span className="inline-block w-2 h-2 bg-emerald-600 mr-1" /> Actual ≥ 95%</span>
        <span><span className="inline-block w-2 h-2 bg-amber-500 mr-1" /> Actual 80–95%</span>
        <span><span className="inline-block w-2 h-2 bg-red-500 mr-1" /> Actual &lt; 80%</span>
      </div>
    </div>
  );
}

function MSPPanel({ project }) {
  const [tasks, setTasks] = useState([]);
  const [boqItems, setBoqItems] = useState([]);
  const [importing, setImporting] = useState(false);

  const load = async () => {
    setTasks(await apiGet(`/msp/by-project/${project.id}`));
    const b = await apiGet(`/boq/by-project/${project.id}`);
    setBoqItems(b.items);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id]);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    const fd = new FormData();
    fd.append('file', f);
    try {
      const r = await api.post(`/msp/import?project_id=${project.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const synced = r.data.boq_synced || 0;
      toast.success(`Imported ${r.data.imported} ${r.data.format?.toUpperCase()} tasks · synced ${synced} BOQ items`);
      load();
    } catch { toast.error('Import failed — check format'); }
    setImporting(false);
    e.target.value = '';
  };

  const manualSync = async () => {
    try {
      const r = await apiPost(`/msp/sync-progress/${project.id}`, {});
      toast.success(`Synced ${r.synced} BOQ items from MSP %Complete`);
      load();
    } catch { toast.error('Sync failed'); }
  };

  const linkBoq = async (taskId, boqId) => {
    await apiPut(`/msp/${taskId}/link-boq`, { boq_id: boqId });
    toast.success('Linked');
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="kpi-overline">Microsoft Project Import</div>
          <h3 className="font-display font-bold text-base mt-1">{tasks.length} MSP tasks · {tasks.filter(t => t.boq_id).length} linked to BOQ</h3>
        </div>
        <label className="flex items-center gap-2 px-3 py-1.5 text-xs bg-[#3b4872] text-white hover:bg-[#2d3856] rounded-sm hover:bg-[#3b4872] font-display font-semibold cursor-pointer">
    <input data-testid="msp-upload" type="file" accept=".csv,.xml,text/csv,text/xml,application/xml" onChange={onUpload} className="hidden" />
          <Upload size={13} /> {importing ? 'Importing…' : 'Import MSP CSV / XML'}
        </label>
        <button data-testid="msp-sync-btn" onClick={manualSync} className="flex items-center gap-2 px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 font-display font-semibold">
          <RefreshCw size={13} /> Sync %Complete → BOQ
        </button>
      </div>

      <div className="bg-amber-50 border-l-4 border-l-amber-500 px-4 py-3 mb-4 text-xs text-amber-900 flex items-start gap-2">
        <FileSpreadsheet size={14} className="shrink-0 mt-0.5" />
        <div>
          <div className="font-display font-bold uppercase tracking-widest text-[10px] mb-1">MSP → BOQ progress sync</div>
          On import (and manual sync), every MSP task linked to a BOQ item updates that BOQ's executed quantity proportionally:&nbsp;
          <span className="font-mono">executed_qty = quantity × (%Complete ÷ 100)</span>. Earned value and progress refresh automatically.<br />
          <strong className="block mt-1">Supported formats:</strong>
          <strong>CSV:</strong> ID, Name, Start, Finish, Duration, % Complete, Predecessors, Resource Names ·&nbsp;
          <strong>MSP XML:</strong> direct export from MS Project (File → Save As → XML).
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Task ID</th><th>Task Name</th><th>Start</th><th>Finish</th><th>Duration</th><th className="text-right">% Complete</th><th>Predecessors</th><th>Resources</th><th className="min-w-[260px]">Linked BOQ Item</th>
          </tr></thead>
          <tbody>
            {tasks.length === 0 && <tr><td colSpan="9" className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">No MSP tasks imported yet</td></tr>}
            {tasks.map(t => (
              <tr key={t.id}>
                <td className="font-mono text-xs">{t.task_id}</td>
                <td className="text-sm font-medium">{t.name}</td>
                <td className="text-xs">{t.start}</td>
                <td className="text-xs">{t.finish}</td>
                <td className="text-xs">{t.duration}</td>
                <td className="text-right tabular-nums font-display font-bold">{t.percent_complete}</td>
                <td className="font-mono text-[10px] text-slate-500">{t.predecessors || '—'}</td>
                <td className="text-xs text-slate-600">{t.resources || '—'}</td>
                <td>
                  <select data-testid={`msp-link-${t.task_id}`} value={t.boq_id || ''} onChange={(e) => linkBoq(t.id, e.target.value)}
                    className={`w-full px-2 py-1 text-xs border rounded-sm bg-white ${t.boq_id ? 'border-emerald-300 text-emerald-700' : 'border-slate-300 text-slate-500'}`}>
                    <option value="">— not linked —</option>
                    {boqItems.map(b => <option key={b.id} value={b.id}>{b.item_code} · {b.description.slice(0, 40)}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
